// components/ThemedTimePicker.jsx
import React, { useMemo, useRef, useState, useEffect } from "react";

function pad(n){ return String(n).padStart(2,"0"); }

export default function ThemedTimePicker({ value="12:00", onChange, label="เวลาเกิด", disabled=false }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const popRef = useRef(null);

  const [h, m] = useMemo(()=> {
    const [hh, mm] = (value || "12:00").split(":");
    return [Math.min(23, Math.max(0, +hh||0)), Math.min(59, Math.max(0, +mm||0))];
  }, [value]);

  const [hour, setHour] = useState(h);
  const [min, setMin] = useState(m);

  useEffect(()=>{ setHour(h); setMin(m); }, [h,m]);

  useEffect(()=>{
    const onDoc = (e)=>{
      if(!open) return;
      if(popRef.current && !popRef.current.contains(e.target) &&
         anchorRef.current && !anchorRef.current.contains(e.target)){
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return ()=>document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const quick = [
    {label:"เช้า", v:[7,30]}, {label:"สาย", v:[10,30]}, {label:"บ่าย", v:[13,30]},
    {label:"เย็น", v:[17,30]}, {label:"ดึก", v:[22,0]}
  ];

  function apply(hh, mm){
    onChange?.(`${pad(hh)}:${pad(mm)}`);
    setOpen(false);
  }

  return (
    <div className="relative">
      <label className="block text-sm mb-2 text-left">{label}</label>

      <button
        ref={anchorRef}
        type="button"
        disabled={disabled}
        onClick={()=>!disabled && setOpen(v=>!v)}
        className={`w-full h-11 rounded-[14px] bg-transparent px-4 text-white text-left
                    outline-none focus:ring-2 focus:ring-[#FFC94A]/60 disabled:opacity-60`}
        style={{ border: "1.5px solid #FFC94A" }}
      >
        {pad(h)}:{pad(m)}
      </button>

      {open && (
        <div
          ref={popRef}
          className="absolute z-50 mt-2 w-[320px] max-w-[92vw] rounded-2xl p-[1.5px] bg-white/80 shadow-[0_16px_36px_rgba(0,0,0,0.55)]"
        >
          <div className="rounded-2xl p-3" style={{ background: "linear-gradient(180deg,#0E1732 0%, #0B122A 100%)" }}>
            {/* Quick chips */}
            <div className="flex gap-2 flex-wrap mb-3">
              {quick.map(q=>(
                <button
                  key={q.label}
                  type="button"
                  onClick={()=>{ setHour(q.v[0]); setMin(q.v[1]); apply(q.v[0], q.v[1]); }}
                  className="px-3 py-1.5 rounded-full text-xs whitespace-nowrap border border-white/30 text-white/90 hover:bg-white/10"
                >
                  {q.label}
                </button>
              ))}
              <button
                type="button"
                onClick={()=>{ const d=new Date(); apply(d.getHours(), d.getMinutes()); }}
                className="px-3 py-1.5 rounded-full text-xs font-bold text-[#1B1300]"
                style={{ background: "linear-gradient(90deg,#FFD86E 0%,#CF7B00 100%)" }}
              >
                ตอนนี้
              </button>
            </div>

            {/* Pickers */}
            <div className="grid grid-cols-2 gap-8">
              {/* Hour */}
              <div>
                <div className="text-white/80 text-xs mb-1">ชั่วโมง</div>
                <div className="grid grid-cols-4 gap-2 max-h-44 overflow-auto pr-1">
                  {Array.from({length:24}, (_,i)=>i).map(num=>(
                    <button
                      key={num}
                      type="button"
                      onClick={()=>setHour(num)}
                      className={`h-9 rounded-xl text-sm flex items-center justify-center
                                  ${hour===num ? "bg-white text-[#1B1300] font-bold" : "bg-white/10 text-white hover:bg-white/15"}`}
                    >
                      {pad(num)}
                    </button>
                  ))}
                </div>
              </div>
              {/* Minute */}
              <div>
                <div className="text-white/80 text-xs mb-1">นาที</div>
                <div className="grid grid-cols-5 gap-2 max-h-44 overflow-auto pr-1">
                  {Array.from({length:60}, (_,i)=>i).map(num=>(
                    <button
                      key={num}
                      type="button"
                      onClick={()=>setMin(num)}
                      className={`h-9 rounded-xl text-sm flex items-center justify-center
                                  ${min===num ? "bg-white text-[#1B1300] font-bold" : "bg-white/10 text-white hover:bg-white/15"}`}
                    >
                      {pad(num)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between mt-3">
              <button
                type="button"
                onClick={()=>apply(hour, min)}
                className="px-4 py-2 rounded-full text-sm font-extrabold text-[#1B1300]"
                style={{ background: "linear-gradient(90deg,#FFD86E 0%,#CF7B00 100%)" }}
              >
                ตั้งเวลา
              </button>
              <button
                type="button"
                onClick={()=>setOpen(false)}
                className="px-3 py-2 rounded-full text-sm text-white/80 hover:bg-white/10"
              >
                ปิด
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
